<script>
    import { afterUpdate, createEventDispatcher } from 'svelte'
    import Pagination from './pagination.svelte'
    import { syncResponsiveLabels } from '../$methods/responsive.table.methods'

    const dispatch = createEventDispatcher()

    export let items = 0
    export let total = null
    export let loading = false
    export let height = 'auto'
    export let skeletonCols = 5
    export let skeletonRows = 5
    export let metadata = null
    export let page = null

    let tableEl

    $: premiumMode = $$slots.thead || $$slots.header || $$slots.tbody

    afterUpdate(() => syncResponsiveLabels(tableEl))

    function onPageChange() {
        dispatch('change')
    }
</script>

{#if premiumMode}
    <div class="premium-table-wrapper">
        {#if $$slots.header}
            <div class="premium-table-top">
                <slot name="header"></slot>
            </div>
        {/if}

        <div class="table-container" style="overflow: auto; max-height: {height}; margin-bottom: 0;">
            <table bind:this={tableEl} class="table is-fullwidth is-narrow premium-table">
                <slot name="thead"></slot>

                {#if loading}
                    <tbody>
                        {#each Array(skeletonRows) as _}
                            <tr>
                                {#each Array(skeletonCols) as _}
                                    <td>
                                        <div class="skeleton-line"></div>
                                    </td>
                                {/each}
                            </tr>
                        {/each}
                    </tbody>
                {:else if items > 0}
                    <slot name="tbody"></slot>
                {:else}
                    <tbody>
                        <tr>
                            <td colspan="100%" class="empty-state-cell">
                                <div class="empty-state-content">
                                    <div class="empty-state-icon">
                                        <span class="icon is-large">
                                            <i class="fas fa-search fa-2x"></i>
                                        </span>
                                    </div>
                                    <h3 class="empty-state-title">No hay resultados</h3>
                                    <p class="empty-state-subtitle">No se encontraron registros que coincidan con tu búsqueda actual.</p>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                {/if}
            </table>
        </div>

        <div class="premium-table-bottom items-space">
            <div class="records-count">
                {#if items || items === 0}
                    Mostrando <span class="has-text-weight-bold">{items}</span>
                    {#if total}
                        de <span class="has-text-weight-bold">{total}</span>
                    {:else if metadata && metadata.total}
                        de <span class="has-text-weight-bold">{metadata.total}</span>
                    {/if}
                    registros
                {/if}
            </div>

            <div class="is-flex is-align-items-center" style="gap: 1rem;">
                <slot name="actions"></slot>
                {#if metadata || page !== null}
                    <Pagination bind:metadata bind:page on:change={onPageChange} />
                {/if}
            </div>
        </div>
    </div>
{:else}
    <div class="premium-table-wrapper legacy-table">
        <div class="table-container" style="overflow: auto; max-height: {height || 'auto'};">
            {#if loading}
                <table class="table is-fullwidth is-narrow premium-table">
                    <tbody>
                        {#each Array(skeletonRows) as _}
                            <tr>
                                {#each Array(skeletonCols) as _}
                                    <td>
                                        <div class="skeleton-line"></div>
                                    </td>
                                {/each}
                            </tr>
                        {/each}
                    </tbody>
                </table>
            {:else if items > 0}
                <table bind:this={tableEl} class="table is-fullwidth is-narrow premium-table">
                    <slot></slot>
                </table>
            {:else}
                <table class="table is-fullwidth is-narrow premium-table">
                    <tbody>
                        <tr>
                            <td colspan="100%" class="empty-state-cell">
                                <div class="empty-state-content">
                                    <div class="empty-state-icon">
                                        <span class="icon is-large">
                                            <i class="fas fa-search fa-2x"></i>
                                        </span>
                                    </div>
                                    <h3 class="empty-state-title">No hay resultados</h3>
                                    <p class="empty-state-subtitle">No se encontraron registros que coincidan con tu búsqueda actual.</p>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            {/if}
        </div>

        {#if items || items === 0}
            <div class="premium-table-bottom items-space">
                <div class="records-count">
                    Mostrando <span class="has-text-weight-bold">{items}</span>
                    {#if total}
                        de <span class="has-text-weight-bold">{total}</span>
                    {/if}
                    registros
                </div>
            </div>
        {/if}
    </div>
{/if}

<style>
    .premium-table-wrapper {
        background-color: #ffffff;
        border: 1px solid #ececec;
        border-radius: 10px;
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.02);
        overflow: hidden;
        margin-bottom: 1.5rem;
    }

    .premium-table-top {
        padding: 1rem 1.5rem;
        background-color: #fafbfc;
        border-bottom: 1px solid #ececec;
    }

    .premium-table-bottom {
        padding: 1rem 1.5rem;
        border-top: 1px solid #ececec;
        background-color: #ffffff;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .records-count {
        font-size: 0.85rem;
        color: #8b92a1;
    }

    .premium-table {
        margin-bottom: 0;
        background-color: #ffffff;
    }

    .premium-table :global(thead th) {
        font-size: 0.65rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
        color: #9299a6;
        border-bottom: 1px solid #ececec;
        border-top: none;
        padding: 0.85rem 1rem;
        background-color: #fafbfc;
        vertical-align: middle;
    }

    .premium-table :global(tbody td) {
        font-size: 0.9rem;
        color: #2d3748;
        border-bottom: 1px solid #f4f5f7;
        padding: 0.85rem 1rem;
        vertical-align: middle;
    }

    .premium-table :global(tbody tr:last-child td) {
        border-bottom: none;
    }

    .premium-table :global(tbody tr:hover) {
        background-color: #fcfcfd;
    }

    .empty-state-cell {
        padding: 4rem 1rem !important;
        background-color: #fafbfc !important;
    }

    .empty-state-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
    }

    .empty-state-icon {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background-color: #ffffff;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1.25rem;
        color: #a7b0c0;
    }

    .empty-state-title {
        font-size: 1.1rem;
        font-weight: 600;
        color: #2d3748;
        margin-bottom: 0.5rem;
    }

    .empty-state-subtitle {
        font-size: 0.9rem;
        color: #718096;
        max-width: 300px;
    }

    .skeleton-line {
        height: 18px;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: loading 1.5s infinite;
        border-radius: 4px;
        width: 100%;
    }

    @keyframes loading {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
    }
</style>
